import { createApp, reactive, type App as VueApp } from 'vue'
import RootApp from '@/App.vue'
import styles from '@/styles/tailwind.css?inline'
import type { TicketWidgetApi, TicketWidgetInstance, TicketWidgetOptions } from '@/types'

interface MountedWidget {
  app: VueApp
  host: Element
  mountPoint: Element
  options: TicketWidgetOptions
  update: (options: Partial<TicketWidgetOptions>) => void
  unmount: () => void
}

const mountedWidgets = new WeakMap<Element, MountedWidget>()

function mount(target: string | Element, options: TicketWidgetOptions): TicketWidgetInstance {
  const host = resolveTarget(target)

  if (!host) {
    throw new Error('TicketWidget: mount target was not found.')
  }

  const existingWidget = mountedWidgets.get(host)

  if (existingWidget) {
    existingWidget.unmount()
  }

  const resolvedOptions = normalizeOptions(host, options)
  const root = resolvedOptions.shadow === false ? host : getShadowRoot(host)
  clearNode(root)

  if (resolvedOptions.shadow !== false) {
    injectStyles(root)
  }

  const mountPoint = document.createElement('div')
  mountPoint.setAttribute('data-ticket-widget-root', '')
  root.append(mountPoint)

  const state = reactive({ options: resolvedOptions })
  const app = createApp(RootApp, state)
  app.mount(mountPoint)

  const widget: MountedWidget = {
    app,
    host,
    mountPoint,
    options: resolvedOptions,
    update: (nextOptions) => {
      widget.options = normalizeOptions(host, {
        ...widget.options,
        ...nextOptions,
      })
      state.options = widget.options
    },
    unmount: () => {
      app.unmount()
      clearNode(root)
      mountedWidgets.delete(host)
    },
  }

  mountedWidgets.set(host, widget)

  return {
    update: widget.update,
    unmount: widget.unmount,
  }
}

function unmount(target: string | Element): void {
  const host = resolveTarget(target)

  if (!host) {
    return
  }

  mountedWidgets.get(host)?.unmount()
}

function autoMount(root: ParentNode = document): TicketWidgetInstance[] {
  return Array.from(root.querySelectorAll('[data-ticket-widget]')).map((element) =>
    mount(element, optionsFromDataset(element as HTMLElement)),
  )
}

function resolveTarget(target: string | Element): Element | null {
  if (typeof target === 'string') {
    return document.querySelector(target)
  }

  return target
}

function normalizeOptions(host: Element, options: TicketWidgetOptions): TicketWidgetOptions {
  const eventId = options.eventId || (host as HTMLElement).dataset.eventId

  if (!eventId) {
    throw new Error('TicketWidget: eventId is required.')
  }

  return {
    locale: 'ru-RU',
    currency: 'RUB',
    credentials: 'include',
    shadow: true,
    ...options,
    eventId,
  }
}

function optionsFromDataset(element: HTMLElement): TicketWidgetOptions {
  return {
    eventId: element.dataset.eventId ?? '',
    apiBaseUrl: element.dataset.apiBaseUrl,
    locale: element.dataset.locale,
    currency: element.dataset.currency,
    accessToken: element.dataset.accessToken,
  }
}

function getShadowRoot(host: Element): ShadowRoot {
  return host.shadowRoot ?? host.attachShadow({ mode: 'open' })
}

function injectStyles(root: ShadowRoot | Element): void {
  const style = document.createElement('style')
  style.textContent = styles
  root.append(style)
}

function clearNode(node: Element | ShadowRoot): void {
  while (node.firstChild) {
    node.firstChild.remove()
  }
}

const ticketWidgetApi: TicketWidgetApi = {
  version: __TICKET_WIDGET_VERSION__,
  mount,
  unmount,
  autoMount,
}

if (typeof window !== 'undefined') {
  window.TicketWidget = ticketWidgetApi

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => autoMount())
  } else {
    queueMicrotask(() => autoMount())
  }
}

export default ticketWidgetApi
